import type { BaseClient } from '../../http/BaseClient.js';
import { unwrapData } from '../../common/envelope.js';
import type {
  AvailableModels,
  ModelPricingRow,
  ModelSelection,
  PatchModelSelectionRequest,
  PlatformSettings,
  UpdatePlatformSettingsRequest,
} from './types.js';

/**
 * Platform-admin global config. All endpoints are gated server-side on the
 * caller's home org being a platform-admin org. The model-selection / pricing
 * methods are proxied through the backend to the cloud-agent service.
 */
export class PlatformAdminClient {
  constructor(private readonly http: BaseClient) {}

  // --- Global backend settings (open registration + free-tier credits) ---

  /** Current global settings. */
  async getPlatformSettings(): Promise<PlatformSettings> {
    const res = await this.http.get<{ success: boolean; data: PlatformSettings }>('/admin/platform-settings');
    return unwrapData(res);
  }

  /** Update the global settings. */
  async updatePlatformSettings(body: UpdatePlatformSettingsRequest): Promise<PlatformSettings> {
    const res = await this.http.put<{ success: boolean; data: PlatformSettings }>('/admin/platform-settings', {
      body,
    });
    return unwrapData(res);
  }

  /** Models the aigateway currently exposes (for validating selection/pricing). */
  async listAvailableModels(): Promise<AvailableModels> {
    const res = await this.http.get<{ success: boolean; data: AvailableModels }>(
      '/admin/available-models',
    );
    return unwrapData(res);
  }

  // --- Chat-model selection (bridged to cloud-agent) ---

  /** Current model selection (provider priority + per-provider/role models). */
  async getModelSelection(): Promise<ModelSelection> {
    const res = await this.http.get<{ success: boolean; data: ModelSelection }>('/admin/model-selection');
    return unwrapData(res);
  }

  /** Full replace of the model selection. Returns the fresh selection. */
  async putModelSelection(body: ModelSelection): Promise<ModelSelection> {
    const res = await this.http.put<{ success: boolean; data: ModelSelection }>('/admin/model-selection', { body });
    return unwrapData(res);
  }

  /** Upsert a single (provider, role) → model slot. Returns the fresh selection. */
  async patchModelSelection(body: PatchModelSelectionRequest): Promise<ModelSelection> {
    const res = await this.http.patch<{ success: boolean; data: ModelSelection }>('/admin/model-selection', {
      body,
    });
    return unwrapData(res);
  }

  // --- Model pricing / surcharge (bridged to cloud-agent) ---

  /** All model_pricing rows. */
  async listModelPricing(): Promise<ModelPricingRow[]> {
    const res = await this.http.get<{ success: boolean; data: { pricing: ModelPricingRow[] } }>(
      '/admin/model-pricing',
    );
    return unwrapData(res).pricing ?? [];
  }

  /** Upsert one model_pricing row (also used to add a new row). Returns the fresh list. */
  async putModelPricing(row: ModelPricingRow): Promise<ModelPricingRow[]> {
    const res = await this.http.put<{ success: boolean; data: { pricing: ModelPricingRow[] } }>(
      '/admin/model-pricing',
      { body: row },
    );
    return unwrapData(res).pricing ?? [];
  }

  /** Delete a model_pricing row by model id. Returns the fresh list. */
  async deleteModelPricing(model: string): Promise<ModelPricingRow[]> {
    const res = await this.http.delete<{ success: boolean; data: { pricing: ModelPricingRow[] } }>(
      `/admin/model-pricing?model=${encodeURIComponent(model)}`,
    );
    return unwrapData(res).pricing ?? [];
  }
}
